"use client"

import Link from "next/link"
import { IconChevronRight, IconFlask } from "@tabler/icons-react"
import { useTranslations } from "next-intl"
import { useFormatter } from "@/hooks/use-formatter"

import { ApolloPanel } from "@/components/ui/apollo-panel"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { LabOrderStatusBadge } from "@/components/lab-orders/lab-order-status-badge"
import { cn } from "@/lib/utils"
import type { LabOrder, LabOrderMaterial } from "@/types"
import { formatMaterialGroups, labOrderMaterialGroups } from "@/lib/lab-order-materials"

// Static literal-key map (T-73-05: never t(materialVar)) — resolved against the
// existing `labOrders` namespace so the copy stays in one place.
const MATERIAL_KEY: Record<LabOrderMaterial, string> = {
  metal_ceramic: "material.metalCeramic",
  laser_sinter: "material.laserSinter",
  emax: "material.emax",
  implant: "material.implant",
  zirconia: "material.zirconia",
  inlay_onlay: "material.inlayOnlay",
  acetal: "material.acetal",
}

interface LabOrdersInFlightProps {
  /** The in-flight page (sent → ready), soonest due first. */
  orders: LabOrder[]
  /** Server-side total for the same filter — the page may be truncated. */
  total: number
  isLoading: boolean
  className?: string
}

/** `yyyy-MM-dd` → local midnight. `new Date("2026-07-30")` would parse as UTC. */
function parseLocalDate(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value)
  if (!match) return null
  const date = new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
  )
  return Number.isNaN(date.getTime()) ? null : date
}

function startOfToday(): Date {
  const now = new Date()
  now.setHours(0, 0, 0, 0)
  return now
}

const DAY_MS = 86_400_000

/**
 * Doctor dashboard lab rail — the in-flight orders (everything past draft but
 * not yet delivered/cancelled), soonest due first, overdue in a danger tone.
 *
 * Presentational; the parent owns the permission-gated fetch. Note the feed is
 * clinic-wide by design: the API's `scope=mine` filter keys on
 * `assigned_technician_id`, which is the LAB's queue — a dentist is the
 * `dentist_id`, so scoping to "mine" here would always come back empty.
 */
export function LabOrdersInFlight({
  orders,
  total,
  isLoading,
  className,
}: LabOrdersInFlightProps) {
  const t = useTranslations("dashboard")
  const tLab = useTranslations("labOrders")
  const formatter = useFormatter()

  if (isLoading) {
    return (
      <ApolloPanel eyebrow={t("labInFlight.title")} className={className}>
        <div className="space-y-3">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="flex items-center gap-3">
              <Skeleton className="h-4 flex-1" />
              <Skeleton className="h-5 w-20 shrink-0 rounded-full" />
              <Skeleton className="h-4 w-16 shrink-0" />
            </div>
          ))}
        </div>
      </ApolloPanel>
    )
  }

  if (orders.length === 0) {
    return (
      <ApolloPanel eyebrow={t("labInFlight.title")} className={className}>
        <div className="flex h-[180px] flex-col items-center justify-center text-center">
          <div className="mb-3 rounded-full bg-muted p-3">
            <IconFlask className="h-6 w-6 text-muted-foreground" />
          </div>
          <p className="text-sm font-medium text-muted-foreground">
            {t("labInFlight.empty.title")}
          </p>
          <p className="mt-1 text-xs text-muted-foreground/70">
            {t("labInFlight.empty.body")}
          </p>
        </div>
      </ApolloPanel>
    )
  }

  const today = startOfToday()

  return (
    <ApolloPanel
      eyebrow={t("labInFlight.title")}
      actions={
        <Badge variant="outline" className="text-[10px]">
          {t("labInFlight.count", { count: total })}
        </Badge>
      }
      className={className}
    >
      <div className="space-y-1">
        {orders.map((order) => {
          const due = order.due_date ? parseLocalDate(order.due_date) : null
          const diffDays = due
            ? Math.round((due.getTime() - today.getTime()) / DAY_MS)
            : null
          const isOverdue = diffDays !== null && diffDays < 0

          let dueLabel: string
          if (!due || diffDays === null) dueLabel = t("labInFlight.noDueDate")
          else if (diffDays === 0) dueLabel = t("labInFlight.dueToday")
          else dueLabel = formatter.relativeTime(due, today)

          return (
            <Link
              key={order.id}
              href={`/lab-orders/${order.id}`}
              className="group flex items-center gap-2.5 rounded-md px-2 py-1.5 transition-colors hover:bg-muted/60"
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">
                  {order.lead?.full_name || order.order_number}
                </span>
                <span className="block truncate text-[11px] text-text-dim">
                  {formatMaterialGroups(labOrderMaterialGroups(order), (m) => tLab(MATERIAL_KEY[m]))} · {order.order_number}
                </span>
              </span>
              {/* ArtDent 261007 — the doctor's own step: the resin try-in is
                  waiting for "Try-in OK" or "Needs changes" on the order. */}
              {order.phases?.some((p) => p.status === "active" && p.kind === "tryin") ? (
                <Badge className="shrink-0 text-[10px]">{t("labInFlight.tryinToConfirm")}</Badge>
              ) : (
              <LabOrderStatusBadge
                status={order.status}
                className="hidden shrink-0 text-[10px] sm:inline-flex"
              />
              )}
              <span
                className={cn(
                  "shrink-0 text-[11px] tabular-nums",
                  isOverdue
                    ? "font-medium text-red-600 dark:text-red-400"
                    : "text-text-dim",
                )}
              >
                {dueLabel}
              </span>
            </Link>
          )
        })}
      </div>

      <div className="mt-3 border-t border-border pt-3">
        <Button asChild variant="ghost" size="sm" className="w-full justify-between">
          <Link href="/lab-orders">
            {t("labInFlight.viewAll")}
            <IconChevronRight className="h-4 w-4" />
          </Link>
        </Button>
      </div>
    </ApolloPanel>
  )
}
